import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { PLAYER_COLORS } from '@sheepocalypse/shared';
import type { GameState, GameAction, Lobby } from '@sheepocalypse/shared';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || window.location.origin;

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
  uiError: string | null;
  connect: () => void;
  disconnect: () => void;
  clearUiError: () => void;
  joinLobby: (lobbyId: string, playerId: string) => void;
  configureLobby: (data: { rules?: Record<string, unknown>; maxPlayers?: number }) => void;
  setPlayerColor: (color: string) => void;
  addAIPlayer: (data: { strategy: string; name?: string; color?: string }) => void;
  leaveLobby: () => void;
  leaveGame: () => void;
  createGame: () => void;
  submitAction: (action: GameAction) => void;
  requestValidActions: () => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  connectionError: null,
  uiError: null,

  connect: () => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: true,
    });

    socket.on('connect', () => {
      set({ isConnected: true, connectionError: null });
    });

    socket.on('disconnect', () => {
      set({ isConnected: false });
    });

    socket.on('connect_error', err => {
      console.error('Connection error:', err);
      set({ connectionError: 'Failed to connect to server' });
    });

    // Lobby events
    socket.on('lobby:joined', (data: { lobby: Lobby }) => {
      useLobbyStore.getState().setCurrentLobby(data.lobby);
    });

    socket.on('lobby:updated', (lobby: Lobby) => {
      useLobbyStore.getState().setCurrentLobby(lobby);
    });

    // Game events
    socket.on('game:started', (data: { gameState: GameState }) => {
      useGameStore.getState().setGameState(data.gameState);
    });

    socket.on(
      'game:state-update',
      (data: { gameState: GameState; lastAction: GameAction; isAI?: boolean }) => {
        useGameStore.getState().setGameState(data.gameState);
        useGameStore.getState().setLastAction(data.lastAction);
      }
    );

    socket.on('game:valid-actions', (data: { actions: GameAction[] }) => {
      useGameStore.getState().setValidActions(data.actions);
    });

    socket.on('game:reconnected', (data: { gameState: GameState }) => {
      useGameStore.getState().setGameState(data.gameState);
    });

    socket.on('error', (data: { message: string }) => {
      console.error('Server error:', data.message);
      set({ uiError: data.message });
    });

    socket.on('game:action-failed', (data: { error: string }) => {
      set({ uiError: data.error });
    });

    set({ socket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  clearUiError: () => {
    set({ uiError: null });
  },

  joinLobby: (lobbyId: string, playerId: string) => {
    const { socket } = get();
    if (socket) {
      socket.emit('lobby:join', { lobbyId, playerId });
    }
  },

  createGame: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:start');
    }
  },

  configureLobby: data => {
    const { socket } = get();
    if (socket) {
      socket.emit('lobby:configure', data);
    }
  },

  setPlayerColor: color => {
    const { socket } = get();
    if (socket) {
      socket.emit('lobby:set-color', { color });
    }
  },

  addAIPlayer: data => {
    const { socket } = get();
    if (socket) {
      socket.emit('lobby:add-ai', data);
    }
  },

  leaveLobby: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('lobby:leave');
    }

    useLobbyStore.getState().setCurrentLobby(null);
    useLobbyStore.getState().setPlayerId(null);
    useGameStore.getState().setGameState(null);
    useGameStore.getState().setValidActions([]);
    useGameStore.getState().setCardTargetingCardId(null);
  },

  leaveGame: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:leave');
    }

    useGameStore.getState().setGameState(null);
    useGameStore.getState().setValidActions([]);
    useGameStore.getState().setPlacementFilter(null);
    useGameStore.getState().setCardTargetingCardId(null);
    useLobbyStore.getState().setCurrentLobby(null);
    useLobbyStore.getState().setPlayerId(null);
  },

  submitAction: (action: GameAction) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:action', { action });
    }
  },

  requestValidActions: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:get-actions');
    }
  },
}));

interface GameStore {
  gameState: GameState | null;
  lastAction: GameAction | null;
  validActions: GameAction[];
  placementFilter: string | null;
  cardTargetingCardId: string | null;
  setGameState: (state: GameState | null) => void;
  setLastAction: (action: GameAction | null) => void;
  setValidActions: (actions: GameAction[]) => void;
  setPlacementFilter: (filter: string | null) => void;
  setCardTargetingCardId: (cardId: string | null) => void;
}

export const useGameStore = create<GameStore>(set => ({
  gameState: null,
  lastAction: null,
  validActions: [],
  placementFilter: null,
  cardTargetingCardId: null,
  setGameState: state => set({ gameState: state }),
  setLastAction: action => set({ lastAction: action }),
  setValidActions: actions => set({ validActions: actions }),
  setPlacementFilter: filter => set({ placementFilter: filter }),
  setCardTargetingCardId: cardTargetingCardId => set({ cardTargetingCardId }),
}));

interface LobbyStore {
  currentLobby: Lobby | null;
  playerId: string | null;
  draftName: string;
  draftColor: string;
  setCurrentLobby: (lobby: Lobby | null) => void;
  setPlayerId: (id: string | null) => void;
  setDraftName: (name: string) => void;
  setDraftColor: (color: string) => void;
}

export const useLobbyStore = create<LobbyStore>(set => ({
  currentLobby: null,
  playerId: null,
  draftName: '',
  draftColor: PLAYER_COLORS[0],
  setCurrentLobby: lobby => set({ currentLobby: lobby }),
  setPlayerId: id => set({ playerId: id }),
  setDraftName: draftName => set({ draftName }),
  setDraftColor: draftColor => set({ draftColor }),
}));
